import { ReactElement, useState } from 'react'

import { useTheme } from '@emotion/react'
import Select, { components, DropdownIndicatorProps, SingleValue } from 'react-select'

import { ExpandIcon } from '@arcade/web/icons/react'

export type DropdownInputProps<T> = {
  autoFocus?: boolean
  options: T[]
  formValue: T | null
  handleChange: (option: T | null, action: string, onChange?: (value: SingleValue<T> | null) => void) => void
  field?: { onChange: (value: SingleValue<T> | null) => void }
  placeholder?: string
  ariaLabel?: string
  className?: string
  isError?: boolean
}

const DropdownIndicator = <T,>(props: DropdownIndicatorProps<T, false>) => {
  return (
    <components.DropdownIndicator {...props}>
      <ExpandIcon size={24} />
    </components.DropdownIndicator>
  )
}

/**
 * @deprecated Use ArcadeInputDropdown instead
 */
export const DropdownInput = <
  T extends {
    inputLabel: ReactElement
  }
>({
  autoFocus,
  options,
  formValue,
  handleChange,
  field,
  placeholder,
  ariaLabel,
  className,
  isError = false,
}: DropdownInputProps<T>) => {
  const theme = useTheme()
  const [isMenuOpen, setIsMenuOpen] = useState(false)

  const SelectWithType = Select<T>

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    // When the Escape key is pressed and the menu is open, stop propagation
    // This prevents the modal from closing when a dropdown inside it is closed with ESC
    if (event.key === 'Escape' && isMenuOpen) {
      event.stopPropagation()
    }
  }

  return (
    <SelectWithType
      {...field}
      value={formValue ? formValue : null} // If we want to clear the value, we must use `null`
      onChange={(option, { action }) => handleChange(option, action, field?.onChange)}
      autoFocus={autoFocus}
      aria-label={ariaLabel}
      placeholder={placeholder}
      isSearchable={false}
      minMenuHeight={0}
      menuPosition="absolute"
      options={options}
      onKeyDown={handleKeyDown}
      onMenuOpen={() => setIsMenuOpen(true)}
      onMenuClose={() => setIsMenuOpen(false)}
      components={{
        DropdownIndicator,
        SingleValue: ({ data }: { data: T }) => data.inputLabel,
      }}
      unstyled
      className={className}
      styles={{
        container: base => {
          return {
            ...base,
            fontSize: '1rem',
            lineHeight: '1.375rem',
            fontWeight: theme.weights.regular,
            caretColor: theme.colors.primary,
            color: theme.colors.body,
          }
        },
        menuPortal: base => ({ ...base, zIndex: 99999 }),
        dropdownIndicator: base => ({ ...base, paddingRight: '0.25rem' }),

        control: (base, props) => {
          const { isFocused } = props

          let stateSpecificStyles = {}
          if (isFocused) {
            stateSpecificStyles = {
              ...stateSpecificStyles,
              boxShadow: `0 0 0 0.125rem ${isError ? theme.colors.error : theme.colors.primary} inset`,
            }
          } else {
            stateSpecificStyles = {
              ...stateSpecificStyles,
              backgroundColor: theme.colors.formFieldBackground,
              boxShadow: `0 0 0 0.125rem ${isError ? theme.colors.error : theme.colors.formFieldBackground} inset`,
              cursor: 'pointer',
            }
          }

          return {
            ...base,
            borderRadius: '1rem',
            padding: '0.75rem',
            cursor: 'pointer',
            outline: 'none',
            transition: 'none',
            ...stateSpecificStyles,
          }
        },
        input: base => {
          return {
            ...base,
            lineHeight: '1.125rem',
            fontSize: '1.125rem',
            marginLeft: '0.5rem',
          }
        },
        menu: base => {
          return {
            ...base,
            backgroundColor: theme.colors.background,
            border: `1px solid ${theme.colors.outline}`,
            borderRadius: '0.75rem',
            overflow: 'hidden',
            top: 'calc(100% + 0.5rem)',
            boxShadow: '0px 12px 24px rgba(0, 0, 0, 0.04)',
          }
        },
        menuList: base => {
          return {
            ...base,
            maxHeight: '16rem',
          }
        },
        option: (base, props) => {
          const { isFocused } = props

          let stateSpecificStyles = {}
          if (isFocused) {
            stateSpecificStyles = {
              ...stateSpecificStyles,
              backgroundColor: theme.colors.behindBackground,
            }
          }

          return {
            ...base,
            borderBottom: `0.0625rem solid ${theme.colors.outline}`,
            cursor: 'pointer',
            ...stateSpecificStyles,

            '&:last-child': {
              borderBottom: 'none',
            },
          }
        },
        placeholder: base => {
          return {
            ...base,
            lineHeight: '1.125rem',
            fontSize: '1.125rem',
            color: theme.colors.placeholderLabel,
            marginLeft: '0.375rem',
          }
        },
        valueContainer: base => {
          return {
            ...base,
            display: 'flex',
            flexWrap: 'nowrap',
            fontSize: '1.125rem',
            color: theme.colors.label,
            minHeight: '2.5rem',
          }
        },
      }}
    />
  )
}
